import { config } from './config';
import { CHALLENGE, type SignupClientType } from './constants';
import type { AuthSession, TokenPayload } from '../types/auth';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function buildUserProfile(idToken: string) {
  const payload = parseJwt(idToken);
  const facilityIdsRaw = payload?.['custom:facility_ids'];
  let facilityIds: string[] = [];
  if (typeof facilityIdsRaw === 'string') {
    try {
      facilityIds = JSON.parse(facilityIdsRaw) as string[];
    } catch {
      facilityIds = [];
    }
  }

  return {
    email: String(payload?.email ?? payload?.['cognito:username'] ?? ''),
    username: String(payload?.['cognito:username'] ?? payload?.email ?? ''),
    groups: Array.isArray(payload?.['cognito:groups'])
      ? (payload?.['cognito:groups'] as string[])
      : [],
    facilityId: payload?.['custom:facility_id'] ? String(payload['custom:facility_id']) : null,
    facilityIds,
  };
}

export function getSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(config.storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

export function saveSessionFromTokens(
  tokens: TokenPayload,
  opts?: { authSurface?: 'general' | 'partner' },
): AuthSession {
  const existing = getSession();
  const session: AuthSession = {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? existing?.refreshToken,
    expiresAt: Date.now() + tokens.expires_in * 1000,
    loginAt: existing?.loginAt ?? Date.now(),
    user: buildUserProfile(tokens.id_token),
    authSurface: opts?.authSurface ?? existing?.authSurface ?? 'general',
  };
  window.localStorage.setItem(config.storageKey, JSON.stringify(session));
  return session;
}

export function clearSession(): void {
  window.localStorage.removeItem(config.storageKey);
}

export function isAuthenticated(): boolean {
  const session = getSession();
  return Boolean(session && Date.now() < session.expiresAt);
}

export function getIdToken(): string | null {
  const session = getSession();
  if (!session) return null;
  if (Date.now() >= session.expiresAt) return null;
  return session.idToken;
}

async function identityFetch<T>(
  path: string,
  options: RequestInit = {},
  auth = true,
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');

  if (auth) {
    const token = getIdToken();
    if (!token) throw new ApiError('Not authenticated');
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${config.identityApiBase}${path}`, {
    ...options,
    headers,
  });

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    throw new ApiError(
      String(data.error_code ?? data.message ?? 'Request failed'),
      String(data.error_code ?? ''),
      response.status,
      data,
    );
  }

  return data as T;
}

export async function createAuthCode(input: {
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod?: string;
  clientId: string;
  state?: string;
  path?: string;
}): Promise<{ code: string; state?: string; expires_in: number }> {
  const session = getSession();
  if (!session) throw new ApiError('Not authenticated');

  const path = input.path ?? '/auth/code';
  const response = await fetch(`${config.identityApiBase}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
    },
    body: JSON.stringify({
      redirect_uri: input.redirectUri,
      code_challenge: input.codeChallenge,
      code_challenge_method: input.codeChallengeMethod ?? 'S256',
      client_id: input.clientId,
      state: input.state,
      tokens: {
        access_token: session.accessToken,
        id_token: session.idToken,
        refresh_token: session.refreshToken,
        expires_in: Math.max(0, Math.floor((session.expiresAt - Date.now()) / 1000)),
      },
    }),
  });

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(
      String(data.error_code ?? data.message ?? 'Request failed'),
      String(data.error_code ?? ''),
      response.status,
      data,
    );
  }

  return {
    code: String(data.code ?? ''),
    state: data.state ? String(data.state) : undefined,
    expires_in: Number(data.expires_in ?? 120),
  };
}

export async function partnerLogin(email: string, password: string) {
  return identityFetch<{
    ok: boolean;
    challenge?: string;
    session?: string;
    challenge_parameters?: Record<string, string>;
    tokens?: TokenPayload;
  }>(
    '/partner/login',
    {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    },
    false,
  );
}

export async function partnerLoginChallenge(email: string, session: string, newPassword: string) {
  return identityFetch<{
    ok: boolean;
    tokens?: TokenPayload;
    challenge?: string;
    session?: string;
  }>(
    '/partner/login/challenge',
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        session,
        new_password: newPassword,
        challenge: CHALLENGE.NEW_PASSWORD_REQUIRED,
      }),
    },
    false,
  );
}

export async function completePartnerSetup(token: string, password: string) {
  return identityFetch<{ ok: boolean }>(
    '/partner/setup/complete',
    {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    },
    false,
  );
}

export async function completePartnerReset(token: string, password: string) {
  return identityFetch<{ ok: boolean }>(
    '/partner/reset-password/complete',
    {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    },
    false,
  );
}

export async function partnerRefreshSession() {
  const session = getSession();
  if (!session?.refreshToken) throw new ApiError('No refresh token');

  const data = await identityFetch<{ ok: boolean; tokens: TokenPayload }>(
    '/partner/refresh',
    {
      method: 'POST',
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    },
    false,
  );

  return saveSessionFromTokens(data.tokens, { authSurface: 'partner' });
}

export async function login(email: string, password: string) {
  return identityFetch<{
    ok: boolean;
    challenge?: string;
    session?: string;
    challenge_parameters?: Record<string, string>;
    tokens?: TokenPayload;
  }>(
    '/login',
    {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    },
    false,
  );
}

export async function loginChallenge(email: string, session: string, newPassword: string) {
  return identityFetch<{
    ok: boolean;
    tokens?: TokenPayload;
    challenge?: string;
    session?: string;
  }>(
    '/login/challenge',
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        session,
        new_password: newPassword,
        challenge: CHALLENGE.NEW_PASSWORD_REQUIRED,
      }),
    },
    false,
  );
}

export async function refreshSession() {
  const session = getSession();
  if (!session?.refreshToken) throw new ApiError('No refresh token');

  const data = await identityFetch<{ ok: boolean; tokens: TokenPayload }>(
    '/refresh',
    {
      method: 'POST',
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    },
    false,
  );

  return saveSessionFromTokens(data.tokens);
}

export interface SignupInput {
  email: string;
  password: string;
  displayName: string;
  clientType: SignupClientType;
  address: string;
  nit: string;
  city: string;
  country: string;
}

export async function signup(input: SignupInput) {
  return identityFetch<{ ok: boolean; email: string; user_confirmed: boolean }>(
    '/signup',
    {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        display_name: input.displayName,
        client_type: input.clientType,
        address: input.address,
        nit: input.nit,
        city: input.city,
        country: input.country,
      }),
    },
    false,
  );
}

export async function confirmSignup(email: string, code: string) {
  return identityFetch<{ ok: boolean; status?: string; client_id?: string }>(
    '/confirm',
    {
      method: 'POST',
      body: JSON.stringify({ email, confirmation_code: code }),
    },
    false,
  );
}

export async function resendSignupCode(email: string) {
  return identityFetch<{ ok: boolean; email: string; retry_after_seconds: number }>(
    '/signup/resend',
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    },
    false,
  );
}

// Password recovery is unauthenticated: Cognito ForgotPassword emails a code, then
// ConfirmForgotPassword resets the password with that code.
export async function forgotPassword(email: string) {
  return identityFetch<{ ok: boolean; message: string }>(
    '/forgot-password',
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    },
    false,
  );
}

export async function resetPassword(email: string, code: string, newPassword: string) {
  return identityFetch<{ ok: boolean; message: string }>(
    '/reset-password',
    {
      method: 'POST',
      body: JSON.stringify({ email, confirmation_code: code, password: newPassword }),
    },
    false,
  );
}

export async function partnerForgotPassword(email: string) {
  return identityFetch<{ ok: boolean; message: string }>(
    '/partner/forgot-password',
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    },
    false,
  );
}

export async function partnerConfirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
) {
  return identityFetch<{ ok: boolean; message: string }>(
    '/partner/forgot-password/confirm',
    {
      method: 'POST',
      body: JSON.stringify({ email, confirmation_code: code, password: newPassword }),
    },
    false,
  );
}

// Self-service account calls are authenticated (Authorization: Bearer id token) so the backend
// can resolve the caller (and enforce master-only on email), while the access token in the body
// authorizes the underlying Cognito operation.
export async function changePassword(previousPassword: string, proposedPassword: string) {
  const session = getSession();
  if (!session?.accessToken) throw new ApiError('Not authenticated');
  return identityFetch<{ ok: boolean; message: string }>('/account/password', {
    method: 'POST',
    body: JSON.stringify({
      access_token: session.accessToken,
      previous_password: previousPassword,
      proposed_password: proposedPassword,
    }),
  });
}

export async function changeEmail(newEmail: string) {
  const session = getSession();
  if (!session?.accessToken) throw new ApiError('Not authenticated');
  return identityFetch<{ ok: boolean; message: string; email: string }>('/account/email', {
    method: 'POST',
    body: JSON.stringify({ access_token: session.accessToken, email: newEmail }),
  });
}

export async function confirmEmailChange(code: string) {
  const session = getSession();
  if (!session?.accessToken) throw new ApiError('Not authenticated');
  return identityFetch<{ ok: boolean; message: string }>('/account/email/verify', {
    method: 'POST',
    body: JSON.stringify({ access_token: session.accessToken, confirmation_code: code }),
  });
}

/** Self-service display alias. Empty string clears. Auth via Bearer id token only. */
export async function setAccountAlias(alias: string) {
  return identityFetch<{ ok: boolean; alias: string; user_id: string }>('/account/alias', {
    method: 'PUT',
    body: JSON.stringify({ alias }),
  });
}

export async function updateClientProfile(input: {
  address: string;
  nit: string;
  city: string;
  country: string;
}) {
  return identityFetch<{
    ok: boolean;
    client_id: string;
    address: string;
    nit: string;
    city: string;
    country: string;
  }>('/account/client', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function logout() {
  const session = getSession();
  if (session?.accessToken || session?.refreshToken) {
    const path = session.authSurface === 'partner' ? '/partner/logout' : '/logout';
    try {
      await identityFetch(
        path,
        {
          method: 'POST',
          body: JSON.stringify({
            access_token: session.accessToken,
            refresh_token: session.refreshToken,
          }),
        },
        false,
      );
    } catch {
      // Best-effort server logout
    }
  }
  clearSession();
}

export { identityFetch };
